import { useContext, useEffect } from "react";
import { Intent } from "@blueprintjs/core";
import { ToasterContext } from "lib/context";
import { madSDK } from "lib/sdk";
import { ensureUserCanLoginFromThisAddress } from "lib/utils/env";

/** How often the session is refreshed while the tab stays visible. */
const KEEP_ALIVE_INTERVAL_MS = 15 * 60 * 1000;
/** Shortest gap between two refreshes, whatever triggered them. */
const REFRESH_THROTTLE_MS = 60 * 1000;

// PK: Though it won't catch all cases, this will detect if a user's browser goes offline
//     Firebase also gives us a check out of box: (e.g "Failed to get document because user is offline")
// eslint-disable-next-line import/prefer-default-export
export const useUserConnectivity = () => {
  const { addToast } = useContext(ToasterContext);
  useEffect(() => {
    const handleOffline = () => {
      addToast({
        intent: Intent.WARNING,
        message:
          "Your internet connection is offline, please check your network settings and try again."
      });
    };

    let lastRefreshedAt: number | null = null;

    const refreshSession = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      // Returning to a tab fires visibilitychange and focus together, and the
      // keep-alive can land on top of either.
      if (
        lastRefreshedAt !== null &&
        Date.now() - lastRefreshedAt < REFRESH_THROTTLE_MS
      ) {
        return;
      }
      lastRefreshedAt = Date.now();
      madSDK.authentication
        .trySession(ensureUserCanLoginFromThisAddress)
        .catch(() => {
          // A refresh that fails leaves the user on the screen they were on;
          // the next guarded action is what surfaces the expired session.
        });
    };

    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", refreshSession);
    window.addEventListener("focus", refreshSession);
    const keepAlive = setInterval(refreshSession, KEEP_ALIVE_INTERVAL_MS);

    return () => {
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", refreshSession);
      window.removeEventListener("focus", refreshSession);
      clearInterval(keepAlive);
    };
  }, [addToast]);
};
